/**
 * The organization's teams, and one of them.
 *
 * A team is a **name you write into a grant** — that is the whole of it, and
 * the page says so rather than leaving somebody to discover it. It carries no
 * settings, and it grants no licence: a seat is the licence, so taking somebody
 * out of a team takes away the workspaces that team reaches and never their
 * key. The note above the member list is that sentence, because it is the one
 * thing an administrator will otherwise assume wrongly.
 *
 * Every team here is one somebody made. An organization opens with none — a
 * workspace reaches whoever its access names, and a team that held everybody by
 * default would be a sharing rule nobody chose. An organization that wants one
 * makes it, names it, and puts people in it like any other.
 */
import { useCallback, useEffect, useState } from 'react';
import type React from 'react';
import { color, font } from '../../theme/tokens';
import { HeaderButton } from '../../components/Shell';
import { Modal, useConfirm } from '../../components/Modal';
import { List, ListEmpty, ListMeta, ListName, ListRow, Note, PageBar, PageBody, PageLede } from '../../components/list';
import { RowAction, StatusCell } from '../../components/primitives';
import { api, apiPath } from '../../lib/api';
import { initialsOf, since } from '../../lib/format';
import type { Organization, TeamList, TeamRecord, TeamSummary } from '../../lib/types';
import { actorLabel, failureText } from './shared';
import { AddToTeam } from './team-add';
import { PageError, SkeletonPage, SkeletonRows, useLoadPhase } from '../../components/Loading';
import { useMotionMount } from '../../lib/presence';

const count = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/** Team · Members · Workspaces · Made by, and the chevron's column (§40.7). */
const TEAM_COLUMNS = 'minmax(0,1.4fr) 116px 124px minmax(0,1fr)';

export function Teams({
  organization,
  onOpen
}: {
  organization: Organization;
  onOpen: (teamId: string) => void;
}): React.JSX.Element {
  const [teams, setTeams] = useState<TeamSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The name being typed, or `null` while nothing is being named. A dialog of
  // our own rather than `window.prompt`: it says what the name is for, it can
  // show a refusal beside the field that caused it, and it does not freeze the
  // page it was opened from.
  const [naming, setNaming] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const answer = await api.get<TeamList>(apiPath`/orgs/${organization.id}/teams`);
    setTeams(answer.teams);
  }, [organization.id]);

  useEffect(() => {
    load().catch((failure: unknown) => setError(failureText(failure, 'The teams could not be read.')));
  }, [load]);

  // Nothing for 300 ms, then the skeleton (§33) rather than « Reading … ».
  const phase = useLoadPhase(teams === null && !error);

  const create = async (): Promise<void> => {
    const name = (naming ?? '').trim();
    if (!name) return;
    setBusy(true);
    setProblem(null);
    try {
      const made = await api.post<TeamSummary>(apiPath`/orgs/${organization.id}/teams`, { name });
      setNaming(null);
      await load();
      onOpen(made.id);
    } catch (failure) {
      setProblem(failureText(failure, 'That team could not be made.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageBar>
        <HeaderButton primary onClick={() => setNaming('')}>
          New team
        </HeaderButton>
      </PageBar>

      {naming !== null && (
        <Modal
          title="A new team"
          lede="A team is a name you write into a workspace’s access. You can add members to it once it exists."
          label="What is it called?"
          value={naming}
          placeholder="Platform"
          onChange={setNaming}
          confirm="Create the team"
          busy={busy}
          problem={problem}
          whenEmpty="Give the team a name."
          onConfirm={() => void create()}
          onCancel={() => {
            setNaming(null);
            setProblem(null);
          }}
        />
      )}

      {teams === null ? (
        error ? (
          <div style={{ padding: '0 var(--al-gutter)' }}>
            <PageError
              onRetry={() =>
                load()
                  .then(() => setError(null))
                  .catch((failure: unknown) => setError(failureText(failure, 'The teams could not be read.')))
              }
            />
          </div>
        ) : (
          phase === 'skeleton' && (
            <div style={{ padding: '0 12px' }}>
              <SkeletonRows columns={TEAM_COLUMNS} />
            </div>
          )
        )
      ) : teams.length === 0 ? (
        <ListEmpty title="No teams yet" />
      ) : (
        <List columns={TEAM_COLUMNS} header={['Team', 'Members', 'Workspaces', 'Made by']}>
          {teams.map((team) => (
            <ListRow key={team.id} columns={TEAM_COLUMNS} onOpen={() => onOpen(team.id)}>
              <ListName
                name={team.name}
                href={`/orgs/${encodeURIComponent(organization.slug)}/teams/${encodeURIComponent(team.id)}`}
                onOpen={() => onOpen(team.id)}
              />
              <ListMeta>{count(team.members, 'member')}</ListMeta>
              <ListMeta>{count(team.workspaces, 'workspace')}</ListMeta>
              <ListMeta>{team.createdBy ? actorLabel(team.createdBy) : '—'}</ListMeta>
            </ListRow>
          ))}
        </List>
      )}

      <p style={{ margin: 0, padding: '18px var(--al-gutter) 30px', maxWidth: 640, fontSize: 12, lineHeight: 1.6, color: color.inkFaint }}>
        A team is a name you write into a workspace’s access. It holds members of this organization and nothing else — no
        settings, and no licence: a seat is the licence, so taking somebody out of a team takes away the workspaces that
        team reaches, never their key.
      </p>
    </>
  );
}

/* ── one team ───────────────────────────────────────────────────────────── */

export function Team({
  organization,
  teamId,
  onName,
  onLeave
}: {
  organization: Organization;
  teamId: string;
  /** The breadcrumb says which team is open, and only this page has read it. */
  onName: (name: string | null) => void;
  onLeave: () => void;
}): React.JSX.Element {
  const [dialog, ask] = useConfirm();
  const [record, setRecord] = useState<TeamRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The member just added, whose row enters with the reveal.
  const [fresh, setFresh] = useState<string | null>(null);
  const reveal = useMotionMount<HTMLDivElement>({ o: 0, y: 28 }, { d: 1, r: 0.55 });
  // What a dialog is open about: a new name being typed, or the deletion being
  // confirmed. Both used to be one of the browser's grey boxes.
  const [renaming, setRenaming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setRecord(await api.get<TeamRecord>(apiPath`/orgs/${organization.id}/teams/${teamId}`));
  }, [organization.id, teamId]);

  useEffect(() => {
    load().catch((failure: unknown) => setError(failureText(failure, 'That team could not be read.')));
  }, [load]);

  const phase = useLoadPhase(!record && !error);

  useEffect(() => {
    onName(record?.team.name ?? null);
    return () => onName(null);
  }, [record, onName]);

  if (!record) {
    return (
      <PageBody>
        {error ? (
          <PageError
            onRetry={() =>
              load()
                .then(() => setError(null))
                .catch((failure: unknown) => setError(failureText(failure, 'That team could not be read.')))
            }
          />
        ) : (
          phase === 'skeleton' && <SkeletonPage />
        )}
      </PageBody>
    );
  }

  const act = async (run: () => Promise<TeamRecord | { ok: true }>): Promise<void> => {
    setError(null);
    try {
      const answer = await run();
      if ('team' in answer) setRecord(answer);
      else onLeave();
    } catch (failure) {
      setError(failureText(failure));
    }
  };

  const rename = async (): Promise<void> => {
    const name = (renaming ?? '').trim();
    if (!name || name === record.team.name) {
      setRenaming(null);
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      setRecord(await api.send<TeamRecord>('PATCH', apiPath`/orgs/${organization.id}/teams/${teamId}`, { name }, {}));
      setRenaming(null);
      await load();
    } catch (failure) {
      setProblem(failureText(failure, 'That name could not be used.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (): Promise<void> => {
    setBusy(true);
    setProblem(null);
    try {
      await api.del<{ ok: true }>(apiPath`/orgs/${organization.id}/teams/${teamId}`);
      setDeleting(false);
      onLeave();
    } catch (failure) {
      setProblem(failureText(failure, 'That team could not be deleted.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {dialog}
      <PageBar>
        <HeaderButton onClick={() => setRenaming(record.team.name)}>Rename</HeaderButton>
        <HeaderButton onClick={() => setDeleting(true)}>Delete</HeaderButton>
      </PageBar>

      {renaming !== null && (
        <Modal
          title={`Rename ${record.team.name}`}
          lede="The workspaces this team reaches keep reaching it — a team is a name, and this changes the name."
          label="What should it be called?"
          value={renaming}
          onChange={setRenaming}
          confirm="Rename the team"
          busy={busy}
          problem={problem}
          whenEmpty="Give the team a name."
          held={renaming.trim() === record.team.name ? 'Type a new name first' : undefined}
          onConfirm={() => void rename()}
          onCancel={() => {
            setRenaming(null);
            setProblem(null);
          }}
        />
      )}

      {deleting && (
        <Modal
          title={`Delete ${record.team.name}?`}
          goes={[`This team’s access to the ${count(record.team.workspaces, 'workspace')} it reaches.`]}
          stays={[
            `${record.team.workspaces === 1 ? 'The workspace itself' : 'The workspaces themselves'}, and the ${count(record.team.members, 'person', 'people')} in the team — nobody loses their seat or their key.`
          ]}
          confirm="Delete the team"
          danger
          busy={busy}
          problem={problem}
          onConfirm={() => void remove()}
          onCancel={() => {
            setDeleting(false);
            setProblem(null);
          }}
        />
      )}

      <PageBody max={900}>
        <PageLede>
          A team holds members of this organization, and gives no licence: a seat is the licence. Taking somebody out of
          here takes away the workspaces this team reaches, not their key.
        </PageLede>

        <Note>
          {record.team.name} reaches{' '}
          {record.team.workspaces === 0 ? 'no workspace yet' : count(record.team.workspaces, 'workspace')}. Which ones is
          decided on each workspace, under Access.
        </Note>

        <div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0,1fr) 120px 130px 90px',
              gap: 16,
              padding: '0 0 8px',
              fontFamily: font.mono,
              fontSize: 10.5,
              letterSpacing: '.12em',
              textTransform: 'uppercase',
              color: color.inkFaint
            }}
          >
            <span>Member</span>
            <span>Seat</span>
            <span>Since</span>
            <span />
          </div>

          {record.members.length === 0 && (
            <div style={{ padding: '14px 0', boxShadow: `inset 0 -1px 0 ${color.hairSoft}` }}>
              <div style={{ fontSize: 13.5, fontWeight: 500, color: color.ink }}>Nobody in {record.team.name} yet</div>
              <p style={{ margin: '2px 0 0', maxWidth: 680, fontSize: 13, lineHeight: 1.45, color: color.inkSubtle }}>
                Add somebody who holds a seat here; the workspaces {record.team.name} reaches open to them at once.
              </p>
            </div>
          )}

          {record.members.map((member) => (
            <div
              key={member.userId}
              // The row just added enters with the reveal and its ground fades
              // from the selected surface in 1.2 s (§40.8, §40.10).
              ref={member.userId === fresh ? reveal : undefined}
              className={member.userId === fresh ? 'al-line-lit' : undefined}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0,1fr) 120px 130px 90px',
                gap: 16,
                alignItems: 'center',
                padding: '11px 0',
                boxShadow: `inset 0 -1px 0 ${color.hairSoft}`
              }}
            >
              <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span
                  aria-hidden="true"
                  style={{
                    flex: 'none',
                    width: 24,
                    height: 24,
                    borderRadius: 999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: color.surfaceAlt,
                    fontFamily: font.mono,
                    fontSize: 9.5,
                    fontWeight: 600,
                    color: color.inkSubtle
                  }}
                >
                  {initialsOf(member.email, member.name)}
                </span>
                <span style={{ minWidth: 0 }}>
                  {member.name && <div style={{ fontSize: 13.5, color: color.ink }}>{member.name}</div>}
                  <div style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint }}>{member.email}</div>
                </span>
              </div>
              {/* The column says SEAT, so it says the seat — not the role. */}
              {member.seatStatus === 'assigned' ? (
                <StatusCell tone="ok" word="Seat" />
              ) : member.seatStatus === 'suspended' ? (
                <StatusCell tone="off" ring word="Deactivated" />
              ) : (
                <StatusCell tone="warn" word="No seat" />
              )}
              <span style={{ fontSize: 12.5, color: color.inkSubtle }}>{since(member.since)}</span>
              <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <RowAction
                  danger
                  onClick={async () => {
                    // Asked first (proposal 13): the workspaces this team reaches go with it.
                    const agreed = await ask({
                      title: `Remove ${member.name ?? member.email} from ${record.team.name}?`,
                      // Item 57h. The validated text says « Her seat, her key … »; the
                      // page knows nobody's pronoun, so the possessive is « their ».
                      goes: [
                        `${(member.name ?? member.email).split(/\s+/)[0]}’s access to the ${count(record.team.workspaces, 'workspace')} ${record.team.name} reaches.`
                      ],
                      stays: ['Their seat, their key and their machines, and any other team they are in.'],
                      confirm: 'Remove',
                      danger: true
                    });
                    if (agreed) void act(() => api.del<TeamRecord>(apiPath`/orgs/${organization.id}/teams/${teamId}/members/${member.userId}`));
                  }}
                >
                  Remove
                </RowAction>
              </span>
            </div>
          ))}
        </div>

        <AddToTeam
          team={record.team.name}
          candidates={record.candidates}
          onAdd={async (userId) => {
            try {
              setRecord(await api.post<TeamRecord>(apiPath`/orgs/${organization.id}/teams/${teamId}/members`, { userId }));
              setFresh(userId);
              return null;
            } catch (failure) {
              return failureText(failure, 'They could not be added.');
            }
          }}
        />

        {error && <p style={{ fontSize: 13, color: color.badText, margin: 0 }}>{error}</p>}
      </PageBody>
    </>
  );
}
